/**
 * Facade for common date operations (cart, formatting, slot checks). The configured
 * instance lives in `./dateCore`; import that module (`helpers/date/dateCore`) when you need the
 * full Day.js API (e.g. `order_times`, transforms). Only `dateCore.ts` imports the `dayjs` package.
 */
import dateCore, { type DateCore } from "./dateCore";

/** Opaque instant type; today backed by Day.js, replaceable later. */
export type DateTime = DateCore;

export const CART_LIFE_IN_DAYS = 10;

export function now(): DateTime {
  return dateCore();
}

export function getCartExpirationUnix(): number {
  return dateCore().add(CART_LIFE_IN_DAYS, "day").unix();
}

/**
 * True if the cart should be treated as expired (with a 20-minute buffer before
 * the stored expiration instant).
 */
export function isCartExpired(dateAsUnix: number): boolean {
  const expirationWithBuffer = dateCore.unix(dateAsUnix).subtract(20, "minute");

  return dateCore().isSameOrAfter(expirationWithBuffer);
}

/** Historical name used by cart persistence; same as `isCartExpired`. */
export const itemExpired = isCartExpired;

export function currentCalendarYear(): number {
  return dateCore().year();
}

export function formatOrderHistoryDate(timePlaced: string): string {
  return dateCore(timePlaced).format("dddd, MMM D");
}

export function formatOrderReadyPromised(orderTimeIso: string): string {
  return dateCore(orderTimeIso).format("dddd, MMMM Do YYYY, h:mm A z");
}

export function formatOrderReadyEstimateWindow(
  orderTimeIso: string,
  estimateLowMinutes: number,
  estimateHighMinutes: number
): string {
  const base = dateCore(orderTimeIso);
  return `${base.add(estimateLowMinutes, "minute").format("h:mm a")} - ${base
    .add(estimateHighMinutes, "minute")
    .format("h:mm a")}`;
}

/**
 * True when the selected "Later" slot is before the soonest allowed submit time
 * (now + fulfillment estimate upper bound).
 */
export function isOrderAheadSlotExpired(
  orderAheadTime: string,
  selectedDate: DateCore,
  estimateWindowMinutes: number
): boolean {
  const orderTime = dateCore(orderAheadTime.replace(/, \w{3}/g, ""), "h:mm A");
  const orderDayAndTime = dateCore(selectedDate.valueOf())
    .hour(orderTime.hour())
    .minute(orderTime.minute())
    .second(0)
    .millisecond(0);

  return orderDayAndTime.isBefore(
    dateCore().add(estimateWindowMinutes, "minute")
  );
}
